import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportException } from '../lib/errorReporting'
import { ErrorFallback } from './ErrorFallback'
import { isChunkLoadError, purgeCaches } from '../lib/chunkRecovery'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Top-level error boundary. Catches synchronous render errors anywhere in
 * the React tree below and renders the branded fallback. Reports the error
 * to Sentry (with React component stack) and to the local console.
 *
 * Class component is required — getDerivedStateFromError and
 * componentDidCatch only exist on class components.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack ?? '' },
      },
    })
    console.error('[ErrorBoundary]', error, errorInfo)
  }

  /**
   * A bare window.location.reload() is guaranteed to fail for a chunk-load
   * error, because the reload is served through the same service worker that
   * holds the bad response. Purge Cache Storage first so Try Again can
   * actually succeed, then reload either way.
   */
  handleReset = (): void => {
    if (isChunkLoadError(this.state.error)) {
      void purgeCaches().finally(() => window.location.reload())
      return
    }
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />
    }
    return this.props.children
  }
}
